import React, { useMemo, useContext } from "react";
import { NavMenu } from "@redriver/cinnamon";
import { Link, useLocation } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import { ArrowIcon, FileIcon, FolderIcon, StarIcon } from "components/icons";
import classNames from "classnames";
import TreeContext from "./TreeContext";
import {
  getDirectorySlug,
  canPreviewFile,
  stripFileExtension,
} from "modules/helpers";
import { FileLink } from "components/buttons";
import queryString from "query-string";

// matches $nav-item-padding-x, the horizontal padding every row starts from before indenting
const basePadding = 14;

// what the sidebar rules select on, so rows that aren't NavMenu.Items still have to carry them
const navItemClasses = "item cin nav-menu-item tree-node";

// previewing happens on the folder's own route, so the previewer opens over its listing
const MenuFileRow = ({ file, folderLink, className, style }) => {
  const contents = (
    <React.Fragment>
      <span className="arrow-wrapper spacer" aria-hidden="true" />
      <FileIcon file={file} className="item-icon" />
      <span className="item-text">{stripFileExtension(file.name)}</span>
    </React.Fragment>
  );

  return canPreviewFile(file) ? (
    <Link
      to={{ pathname: folderLink, search: `?preview=${file.id}` }}
      className={className}
      style={style}
    >
      {contents}
    </Link>
  ) : (
    <FileLink file={file} className={className} style={style}>
      {contents}
    </FileLink>
  );
};

const MenuTreeNode = ({
  node,
  link,
  className,
  onNodeExpanded,
  onLoadMore,
  totalNodes,
  loadingIds,
  level = 0,
  groupLevel = null,
  isGroupExpanded = false,
  isLastGroupItem = false,
  onClick,
  onToggleFavourite,
  renderCustomContent,
  onlyRenderChildren = false,
  showFiles = false,
  collapsible = true,
  asHeader = false,
  indent = 16,
  indentOffset = 0,
}) => {
  const [expandedIds, setExpandedIds] = useContext(TreeContext);
  const previewedFileId = queryString.parse(useLocation().search).preview;
  const isExpanded = useMemo(
    () => !collapsible || expandedIds.some((x) => x == node.id),
    [expandedIds, collapsible]
  );

  // capped so that deeply nested folders don't push their names out of view entirely
  const maxIndentLevel = 10;
  const indentAt = (level) => {
    const depth = Math.min(Math.max(level - indentOffset, 0), maxIndentLevel);
    return depth == 0
      ? basePadding
      : basePadding + indent / 2 + (depth - 1) * indent;
  };

  const directories = node.directories ?? [];
  const files = showFiles ? node.files ?? [] : [];
  const filesCount = showFiles ? node.filesCount ?? 0 : 0;
  const hasChildren = !!node.directoriesCount || !!filesCount;
  const isLoaded = !!directories.length || !!files.length;
  // only meaningful once the documents have arrived; a node awaiting them has none listed yet
  const withheldFiles = node.files != null ? filesCount - files.length : 0;

  // managing group expanded styling
  const isGroupLevel = groupLevel != null && level == groupLevel;
  const groupExpanded = isGroupExpanded || (isGroupLevel && isExpanded);
  const groupClass = groupExpanded ? "group-expanded" : null;
  const groupStart = groupExpanded && isGroupLevel ? "group-start" : null;
  const groupEnd =
    groupExpanded && isLastGroupItem && (!hasChildren || !isExpanded)
      ? "group-end"
      : null;

  // level 1 because the root heading occupies level 0; only sites can be favourited
  const isSite = level == 1 && groupLevel == 1;

  return (
    <React.Fragment>
      {!onlyRenderChildren && (
        <NavMenu.Item
          exact={collapsible}
          link={onClick ? null : link}
          className={classNames(
            "tree-node",
            asHeader ? "tree-header" : null,
            groupClass,
            groupStart,
            groupEnd,
            className
          )}
          onClick={() => {
            onClick != null ? onClick(node.id) : null;
          }}
          style={{
            paddingLeft: indentAt(level),
          }}
        >
          {collapsible && (
            <button
              type="button"
              className="arrow-wrapper"
              disabled={!hasChildren}
              aria-hidden={!hasChildren}
              aria-expanded={hasChildren ? isExpanded : undefined}
              aria-label={
                hasChildren
                  ? `${isExpanded ? "Collapse" : "Expand"} ${node.name}`
                  : undefined
              }
              style={{ visibility: hasChildren ? "visible" : "hidden" }}
              onClick={(e) => {
                e.preventDefault();
                setExpandedIds(
                  isExpanded && isLoaded
                    ? [...expandedIds.filter((x) => x != node.id)]
                    : [...expandedIds, node.id]
                );
                onNodeExpanded(node);
              }}
            >
              <ArrowIcon
                direction={isExpanded && isLoaded ? "down" : "right"}
              />
            </button>
          )}

          {renderCustomContent ? (
            renderCustomContent(node)
          ) : (
            <React.Fragment>
              {isSite && (
                <StarIcon
                  filled={!!node.favourite}
                  className={classNames(
                    "item-star",
                    onToggleFavourite ? "pointer" : null
                  )}
                  aria-label={
                    node.favourite
                      ? `Remove ${node.name} from favourites`
                      : `Add ${node.name} to favourites`
                  }
                  onClick={(e) => {
                    if (!onToggleFavourite) return;
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleFavourite(node);
                  }}
                />
              )}
              {!isSite && !asHeader && <FolderIcon className="item-icon" />}
              <span className="item-text">
                {node.name}
                <Loader
                  active={loadingIds.some((x) => x == node.id)}
                  inline
                  size="mini"
                />
              </span>
            </React.Fragment>
          )}
        </NavMenu.Item>
      )}
      {(isExpanded || !!onlyRenderChildren) &&
        directories.map((x, i) => (
          <MenuTreeNode
            key={x.id}
            node={x}
            link={
              onClick
                ? null
                : `${link}/${getDirectorySlug(x.reference, x.name)}`
            }
            level={level + (onlyRenderChildren ? 0 : 1)}
            className={className}
            onNodeExpanded={onNodeExpanded}
            loadingIds={loadingIds}
            groupLevel={groupLevel}
            isGroupExpanded={groupExpanded}
            isLastGroupItem={
              i == directories.length - 1 &&
              !files.length &&
              (isLastGroupItem || isGroupLevel)
            }
            onClick={onClick}
            onToggleFavourite={onToggleFavourite}
            renderCustomContent={renderCustomContent}
            showFiles={showFiles}
            indent={indent}
            indentOffset={indentOffset}
          />
        ))}
      {(isExpanded || !!onlyRenderChildren) &&
        files.map((x, i) => (
          // a plain link rather than a NavMenu.Item: previewable documents share their folder's
          // path and are told apart by ?preview, which route matching ignores
          <MenuFileRow
            key={x.id}
            file={x}
            folderLink={link}
            className={classNames(
              navItemClasses,
              previewedFileId == x.id ? "active" : null,
              groupExpanded ? "group-expanded" : null,
              i == files.length - 1 &&
                !withheldFiles &&
                (isLastGroupItem || isGroupLevel)
                ? "group-end"
                : null,
              className
            )}
            style={{
              paddingLeft: indentAt(level + 1),
            }}
          />
        ))}
      {(isExpanded || !!onlyRenderChildren) && withheldFiles > 0 ? (
        <Link
          to={link}
          className={classNames(
            navItemClasses,
            "tree-note",
            groupExpanded ? "group-expanded" : null,
            isLastGroupItem || isGroupLevel ? "group-end" : null
          )}
          style={{ paddingLeft: indentAt(level + 1) }}
        >
          <span className="arrow-wrapper spacer" aria-hidden="true" />
          <span className="item-text">
            + {withheldFiles} more document{withheldFiles == 1 ? "" : "s"}
          </span>
        </Link>
      ) : null}
      {(isExpanded || !!onlyRenderChildren) &&
      totalNodes &&
      totalNodes > directories.length &&
      directories.length > 0 ? (
        <NavMenu.Item className="tree-node load-more" onClick={onLoadMore}>
          <span className="item-text">
            Load More
            <Loader
              active={loadingIds.some((x) => x == null)}
              inline
              size="mini"
            />
          </span>
        </NavMenu.Item>
      ) : null}
    </React.Fragment>
  );
};

export default MenuTreeNode;
